import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore, selectUser } from '@/store/authStore';
import { useLogout } from '@/hooks/useAuth';

/**
 * Desktop account dropdown anchored on the navbar user icon.
 *
 * Replaces the old "icon links straight to /profile with no menu" — which is
 * why customers couldn't find their orders. Hover-opens (with a small close
 * delay so the gap to the panel is forgiving) and is fully click/keyboard
 * accessible (toggle on click, Escape + outside-click to close).
 *
 * Items: greeting · My Orders · Wishlist · My Profile · Sign Out.
 */
export function AccountMenu() {
  const user = useAuthStore(selectUser);
  const logout = useLogout();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const closeTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Clear any pending close timer on unmount.
  useEffect(() => () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
  }, []);

  const openNow = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  };
  const closeSoon = () => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 140);
  };

  const handleSignOut = () => {
    setOpen(false);
    logout.mutate(undefined, { onSettled: () => navigate('/') });
  };

  const itemClass =
    'flex items-center gap-3 px-4 py-2.5 text-sm text-[color:var(--color-text-secondary)] transition-colors hover:bg-[color:var(--color-brand-magenta)]/15 hover:text-white focus-visible:bg-[color:var(--color-brand-magenta)]/15 focus-visible:text-white focus:outline-none';

  return (
    <div
      ref={rootRef}
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
    >
      <button
        type="button"
        aria-label="My account"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[color:var(--color-text-secondary)] transition-colors hover:bg-white/[0.06] hover:text-white"
        data-magnetic
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21v-1a8 8 0 0 1 16 0v1" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account"
          className="absolute right-0 top-[calc(100%+8px)] z-[70] w-60 origin-top-right overflow-hidden rounded-xl border border-white/10 bg-[color:var(--color-bg-deep)]/95 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.7)] backdrop-blur-xl"
        >
          <div className="border-b border-white/[0.06] px-4 py-3">
            <p className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--color-text-muted)]">
              Signed in as
            </p>
            <p className="truncate text-sm font-medium text-white">
              {user?.name ?? 'My Account'}
            </p>
          </div>

          <div className="py-1.5">
            <Link to="/orders" role="menuitem" onClick={() => setOpen(false)} className={itemClass}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                <path d="M3 6h18" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              My Orders
            </Link>
            <Link to="/wishlist" role="menuitem" onClick={() => setOpen(false)} className={itemClass}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              Wishlist
            </Link>
            <Link to="/profile" role="menuitem" onClick={() => setOpen(false)} className={itemClass}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21v-1a8 8 0 0 1 16 0v1" />
              </svg>
              My Profile
            </Link>
          </div>

          <div className="border-t border-white/[0.06] py-1.5">
            <button
              type="button"
              role="menuitem"
              onClick={handleSignOut}
              disabled={logout.isPending}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-[color:var(--color-text-secondary)] transition-colors hover:bg-[color:var(--color-error)]/15 hover:text-white focus-visible:bg-[color:var(--color-error)]/15 focus-visible:text-white focus:outline-none disabled:opacity-50"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <path d="M16 17l5-5-5-5" />
                <path d="M21 12H9" />
              </svg>
              {logout.isPending ? 'Signing out…' : 'Sign Out'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
